/**
 * ChainStrip — the Classic view's horizontal chain: one column per strike, a call card
 * above the strike pill and a put card below, each with BUY / SELL. ATM is outlined and
 * centred; ITM cards are tinted; cards for legs already in the basket carry a badge.
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { WsChain, WsRow, atmOf, markOf } from './wsData';
import { WsLeg, OptType, Side } from './wsMath';
import { WsColors, fmtPx, fmtStrike, compactLots } from './wsUi';

interface Props {
  chain: WsChain | null;
  legs: WsLeg[];
  range: number;
  c: WsColors;
  onAdd: (row: WsRow, t: OptType, action: Side) => void;
  recentre: number;
  onChart?: (symbol: string) => void;
}

const COL_W = 92;

const ChainStrip: React.FC<Props> = ({ chain, legs, range, c, onAdd, recentre, onChart }) => {
  const ref = useRef<HTMLDivElement>(null);
  const rowsAll = useMemo(() => chain?.chain?.rows || [], [chain]);
  const spot = chain?.spot || 0;
  const iso = chain?.chain?.iso || '';
  const { strike: atm } = atmOf(rowsAll, spot);
  const rows = useMemo(() => {
    if (!range || atm == null) return rowsAll;
    const i = rowsAll.findIndex(r => r.strike === atm);
    return rowsAll.slice(Math.max(0, i - range), i + range + 1);
  }, [rowsAll, range, atm]);
  const atmIdx = rows.findIndex(r => r.strike === atm);

  const centred = useRef('');
  useEffect(() => {
    const key = `${iso}|${recentre}|${range}`;
    const el = ref.current;
    if (!el || atmIdx < 0 || centred.current === key) return;
    el.scrollLeft = Math.max(0, atmIdx * COL_W - el.clientWidth / 2 + COL_W / 2);
    centred.current = key;
  }, [iso, atmIdx, recentre, range]);

  const badges = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of legs) if (l.expiry === iso) {
      const k = `${l.optionType}|${l.strike}`;
      m.set(k, (m.get(k) ? m.get(k) + ' ' : '') + `${l.action[0]}${l.lots > 1 ? `×${compactLots(l.lots)}` : ''}`);
    }
    return m;
  }, [legs, iso]);

  if (!chain?.chain) return <Box sx={{ p: 2, color: c.dim, fontSize: 12 }}>Loading chain…</Box>;

  const card = (row: WsRow, t: OptType, i: number) => {
    const q = t === 'CE' ? row.ce : row.pe;
    const px = markOf(q);
    const itm = t === 'CE' ? row.strike < spot : row.strike > spot;
    const dist = i - atmIdx;
    const tag = dist === 0 ? 'ATM' : (t === 'CE' ? (dist < 0 ? `ITM${dist}` : `OTM+${dist}`) : (dist > 0 ? `ITM-${dist}` : `OTM+${-dist}`));
    const badge = badges.get(`${t}|${row.strike}`);
    const btn = (action: Side) => (
      <Box component="button" type="button" disabled={px == null}
        onClick={() => px != null && onAdd(row, t, action)}
        sx={{
          all: 'unset', flex: 1, textAlign: 'center', fontSize: 9.5, fontWeight: 700, py: 0.15, borderRadius: 0.5, cursor: px == null ? 'default' : 'pointer',
          color: action === 'BUY' ? c.green : c.red, border: `1px solid ${action === 'BUY' ? c.green : c.red}55`,
          '&:hover': px == null ? {} : { bgcolor: action === 'BUY' ? c.greenBg : c.redBg },
        }}>{action}</Box>
    );
    return (
      <Box sx={{
        height: 86, borderRadius: 1, p: 0.6, display: 'flex', flexDirection: 'column', gap: 0.2, position: 'relative',
        border: `1px solid ${dist === 0 ? c.amber : c.border}`, bgcolor: itm ? c.itm : c.panel,
        opacity: px == null ? 0.45 : 1,
      }}>
        <Box sx={{ fontSize: 9, color: dist === 0 ? c.amber : c.faint, fontWeight: 700 }}>{tag}</Box>
        {badge && <Box sx={{ position: 'absolute', top: 3, right: 4, px: 0.4, fontSize: 9, fontWeight: 700, borderRadius: 0.5, bgcolor: c.amberBg, color: c.amber }}>{badge}</Box>}
        <Box component={q?.symbol && onChart ? 'button' : 'div'} type="button" title={q?.symbol && onChart ? `Chart ${q.symbol}` : undefined}
          onClick={() => q?.symbol && onChart && onChart(q.symbol)}
          sx={{ all: 'unset', alignSelf: 'flex-start', fontSize: 14, fontWeight: 700, color: c.text, fontVariantNumeric: 'tabular-nums', cursor: q?.symbol && onChart ? 'pointer' : 'default', '&:hover': q?.symbol && onChart ? { textDecoration: 'underline' } : {} }}>${fmtPx(px)}</Box>
        <Box sx={{ fontSize: 9.5, color: c.dim, whiteSpace: 'nowrap' }}>IV {q?.iv ? (q.iv * 100).toFixed(0) : '—'} · Δ {q?.delta != null ? q.delta.toFixed(2) : '—'}</Box>
        <Box sx={{ fontSize: 9.5, whiteSpace: 'nowrap' }}>
          <Box component="span" sx={{ color: c.green }}>{fmtPx(q?.bid ?? null)}</Box>
          <Box component="span" sx={{ color: c.faint }}> · </Box>
          <Box component="span" sx={{ color: c.red }}>{fmtPx(q?.ask ?? null)}</Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.4, mt: 'auto' }}>{btn('BUY')}{btn('SELL')}</Box>
      </Box>
    );
  };

  const scrollBy = (d: number) => ref.current?.scrollBy({ left: d * COL_W * 4, behavior: 'smooth' });

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', py: 3.2, px: 0.5 }}>
        <Box sx={{ color: c.call, fontSize: 11, fontWeight: 800, borderLeft: `2px solid ${c.call}`, pl: 0.6 }}>CE</Box>
        <IconButton size="small" onClick={() => scrollBy(-1)}><ChevronLeftIcon fontSize="small" /></IconButton>
        <Box sx={{ color: c.put, fontSize: 11, fontWeight: 800, borderLeft: `2px solid ${c.put}`, pl: 0.6 }}>PE</Box>
      </Box>
      <Box ref={ref} sx={{ flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'hidden', '&::-webkit-scrollbar': { height: 6 } }}>
        <Box sx={{ display: 'flex', gap: 0.6, py: 0.6, px: 0.4, width: 'max-content' }}>
          {rows.map((row, i) => (
            <Box key={row.strike} sx={{ width: COL_W - 5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {card(row, 'CE', i)}
              <Box sx={{
                textAlign: 'center', fontSize: 11, fontWeight: 700, borderRadius: 3, py: 0.1, fontVariantNumeric: 'tabular-nums',
                color: row.strike === atm ? c.amber : c.dim, border: `1px solid ${row.strike === atm ? c.amber : c.border}`,
              }}>{fmtStrike(row.strike)}</Box>
              {card(row, 'PE', i)}
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5 }}>
        <IconButton size="small" onClick={() => scrollBy(1)}><ChevronRightIcon fontSize="small" /></IconButton>
      </Box>
    </Box>
  );
};

export default React.memo(ChainStrip);
